import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Users,
  CheckCircle2,
  Target,
  Package,
  Building,
  Workflow,
  UserPlus,
  Sliders,
  ShieldCheck,
  GitBranch,
  Globe2,
  Eye,
  DollarSign,
  Tag,
  Filter,
  BarChart3,
  PieChart,
  Settings,
  FileText,
  Calendar,
  Bell,
  LogIn,
  Shield,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "../components/utils/Logo CRM.png";
// import dashboardImg from "../components/utils/img/dashboard.jpg";
import contactosImg from "../components/utils/img/Contactos.png";
import empresasImg from "../components/utils/img/Empresas.png";
import oportunidadesImg from "../components/utils/img/Oportunidadess.png";
import productosImg from "../components/utils/img/Productos.png";
import loginVideo from "../components/utils/img/Login.mp4";

export const PublicPlansPage: React.FC = () => {
  const navigate = useNavigate();
  const mainModules = [
    {
      icon: <Users className="h-8 w-8" />,
      title: "Gestión de Contactos",
      description:
        "Registra personas y define el origen del contacto (campaña, formulario, referencia, etc.). Organiza toda la información de tus prospectos y clientes en un solo lugar.",
      image: contactosImg,
      features: [
        "Origen del contacto personalizable",
        "Registro completo de información",
        "Historial de interacciones",
        "Fácil creación y edición",
      ],
    },
    {
      icon: <Building className="h-8 w-8" />,
      title: "Gestión de Empresas",
      description:
        "Registra, edita y administra toda la información de cada empresa cliente. Mantén organizados los datos corporativos y relaciones comerciales.",
      image: empresasImg,
      features: [
        "Información empresarial completa",
        "Múltiples contactos por empresa",
        "Edición sencilla",
        "Administración centralizada",
      ],
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "Oportunidades",
      description:
        "Sistema intuitivo para arrastrar oportunidades entre etapas del embudo de ventas. Crea reglas personalizadas y define tipos de oportunidades según tus necesidades.",
      image: oportunidadesImg,
      features: [
        "Arrastra y suelta entre etapas",
        "Embudos flexibles y personalizables",
        "Configuración de moneda específica",
        "Vistas ordenadas por necesidades",
      ],
    },
    {
      icon: <Package className="h-8 w-8" />,
      title: "Productos",
      description:
        "Catálogo de productos con reglas de venta configurables. Aplica descuentos, promociones y gestiona tu oferta comercial de manera eficiente.",
      image: productosImg,
      features: [
        "Reglas de venta personalizables",
        "Descuentos y promociones",
        "Gestión de catálogo",
        "Precios configurables",
      ],
    },
  ];

  const keyFeatures = [
    {
      icon: <UserPlus className="h-6 w-6" />,
      title: "Gestión de Usuarios",
      description:
        "Crea cuentas para empleados, asigna roles y personaliza permisos. Control total sobre quién ve qué información.",
      color: "bg-blue-100 text-blue-600",
    },
    {
      icon: <GitBranch className="h-6 w-6" />,
      title: "Embudos Múltiples",
      description:
        "Cada vendedor puede tener su propio embudo. Los administradores ven todos los embudos de forma general.",
      color: "bg-indigo-100 text-indigo-600",
    },
    {
      icon: <Sliders className="h-6 w-6" />,
      title: "Configuración Flexible",
      description:
        "Etapas personalizables, reglas específicas y vistas adaptadas a cada necesidad de negocio.",
      color: "bg-purple-100 text-purple-600",
    },
    {
      icon: <BarChart3 className="h-6 w-6" />,
      title: "Métricas de Desempeño",
      description:
        "Visualiza el rendimiento de tu equipo y oportunidades con reportes claros y accesibles.",
      color: "bg-green-100 text-green-600",
    },
    {
      icon: <ShieldCheck className="h-6 w-6" />,
      title: "Roles Personalizados",
      description:
        "Define exactamente qué puede hacer cada usuario en el sistema con permisos granulares.",
      color: "bg-red-100 text-red-600",
    },
    {
      icon: <Eye className="h-6 w-6" />,
      title: "Visibilidad Controlada",
      description:
        "Administradores ven todo, vendedores ven solo lo que les corresponde. Total control de la información.",
      color: "bg-orange-100 text-orange-600",
    },
  ];

  const workflowSteps = [
    {
      number: "1",
      title: "Registro de Contacto",
      description: "Define el origen y registra toda la información",
      icon: <UserPlus className="h-5 w-5" />,
    },
    {
      number: "2",
      title: "Gestión Empresarial",
      description: "Organiza información corporativa completa",
      icon: <Building className="h-5 w-5" />,
    },
    {
      number: "3",
      title: "Seguimiento de Oportunidad",
      description: "Arrastra entre etapas con moneda específica",
      icon: <Target className="h-5 w-5" />,
    },
    {
      number: "4",
      title: "Configuración de Producto",
      description: "Aplica reglas de venta y promociones",
      icon: <Package className="h-5 w-5" />,
    },
    {
      number: "5",
      title: "Gestión de Usuarios",
      description: "Asigna roles y controla visibilidad",
      icon: <Settings className="h-5 w-5" />,
    },
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="Logo CRM"
              className="h-10 sm:h-14 w-auto object-contain"
            />
          </div>
          <div className="flex items-center gap-3 sm:gap-6">
            <Link
              to="/login"
              className="text-xs sm:text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              Iniciar Sesión
            </Link>
            <Link
              to="/register"
              className="bg-white text-slate-900 hover:bg-slate-100 px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-bold transition-all shadow-lg"
            >
              Comenzar
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-12 sm:pt-20 pb-12 sm:pb-20 overflow-hidden bg-white text-slate-900">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-3xl sm:text-6xl lg:text-7xl font-bold mb-6 sm:mb-8 leading-tight">
              Transforma tu proceso de <br />
              <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                ventas con flexibilidad total
              </span>
            </h1>

            <p className="text-base sm:text-xl text-slate-600 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed">
              Kersales es más que un CRM: es una plataforma completa de gestión
              comercial que se adapta a tus procesos. Con módulos intuitivos
              para contactos, empresas, oportunidades y productos, potenciamos
              tu equipo con herramientas flexibles y métricas de desempeño en
              tiempo real.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="group px-8 py-4 bg-slate-900 text-white rounded-full font-bold text-lg hover:bg-slate-800 transition-all shadow-xl flex items-center justify-center gap-2"
              >
                Comenzar 👨🏼‍💻🔜
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Dashboard Preview */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="bg-slate-900 rounded-3xl p-3 shadow-2xl ring-1 ring-slate-900/10">
              <div className="rounded-xl bg-slate-900 aspect-[16/9] overflow-hidden">
                <img
                  // src={dashboardImg}
                  alt="Dashboard Principal Kersales CRM"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Todo lo que necesitas en una plataforma */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4">
              Todo lo que necesitas en una plataforma
            </h2>
            <p className="text-lg text-slate-600">
              Cada módulo está diseñado para trabajar en conjunto, brindando una
              experiencia fluida y completa para tu equipo comercial.
            </p>
          </div>

          <div className="space-y-16">
            {mainModules.map((module, index) => (
              <div
                key={index}
                className={`flex flex-col ${index % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"
                  } items-center gap-12`}
              >
                <div className="w-full lg:w-1/2">
                  <div className="bg-slate-900 rounded-2xl p-3 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1">
                    <div className="rounded-xl bg-white aspect-[4/3] overflow-hidden">
                      {module.image ? (
                        <img
                          src={module.image}
                          alt={`${module.title} - Kersales CRM`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
                          <div className="text-center p-6">
                            <div
                              className={`p-4 rounded-xl ${index === 0
                                ? "bg-blue-100"
                                : index === 1
                                  ? "bg-indigo-100"
                                  : index === 2
                                    ? "bg-purple-100"
                                    : "bg-green-100"
                                } inline-block mb-4`}
                            >
                              {module.icon}
                            </div>
                            <div className="text-slate-400 font-medium">
                              {module.title}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-1/2">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium mb-4">
                    {module.icon}
                    {module.title}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                    {module.title}
                  </h3>
                  <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                    {module.description}
                  </p>
                  <ul className="space-y-3">
                    {module.features.map((feature, idx) => (
                      <li
                        key={idx}
                        className="flex items-center gap-3 text-slate-700"
                      >
                        <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ventajas exclusivas + Características */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4">
              Ventajas exclusivas de Kersales
            </h2>
            <p className="text-lg text-slate-600">
              Diseñado para la flexibilidad que tu negocio necesita
            </p>
          </div>

          <div className="mb-12 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Características que marcan la diferencia
            </h3>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-12">
              Funcionalidades diseñadas específicamente para facilitar la
              gestión comercial
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {keyFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 border border-slate-100"
              >
                <div
                  className={`${feature.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}
                >
                  {feature.icon}
                </div>
                <h3 className="font-bold text-xl text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-slate-50 rounded-3xl p-8 md:p-12 border border-slate-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center text-blue-600 mx-auto mb-6">
                  <Sliders className="h-8 w-8" />
                </div>
                <h3 className="font-bold text-xl text-slate-900 mb-3">
                  Personalización Total
                </h3>
                <p className="text-slate-600">
                  Configura cada aspecto del CRM según tus necesidades
                  específicas de negocio.
                </p>
              </div>

              <div className="text-center p-6">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center text-indigo-600 mx-auto mb-6">
                  <GitBranch className="h-8 w-8" />
                </div>
                <h3 className="font-bold text-xl text-slate-900 mb-3">
                  Multiembudo
                </h3>
                <p className="text-slate-600">
                  Cada equipo puede tener su propio embudo de ventas, con
                  visibilidad controlada.
                </p>
              </div>

              <div className="text-center p-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center text-purple-600 mx-auto mb-6">
                  <ShieldCheck className="h-8 w-8" />
                </div>
                <h3 className="font-bold text-xl text-slate-900 mb-3">
                  Roles y Permisos Granulares
                </h3>
                <p className="text-slate-600">
                  Controla exactamente qué puede ver y hacer cada usuario en el
                  sistema.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Acceso inmediato a tu panel de control
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              En solo segundos estarás dentro de tu CRM. El proceso es rápido,
              seguro y te da acceso instantáneo a todas las herramientas.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="relative">
              <div className="hidden md:block absolute top-6 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-8 relative mb-12">
                {workflowSteps.map((step, index) => (
                  <div key={index} className="text-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                      {step.number}
                    </div>
                    <div className="p-4">
                      <div className="text-blue-600 mb-2 flex justify-center">
                        {step.icon}
                      </div>
                      <h4 className="font-bold text-slate-900 mb-2">
                        {step.title}
                      </h4>
                      <p className="text-sm text-slate-600">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-900 rounded-3xl p-3 shadow-2xl focus:outline-none">
              <div className="rounded-2xl bg-white overflow-hidden relative focus:outline-none">
                <div className="aspect-[16/9] relative overflow-hidden bg-slate-950">
                  {loginVideo ? (
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                    >
                      <source src={loginVideo} type="video/mp4" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center p-8">
                          <LogIn className="h-20 w-20 text-white/50 mx-auto mb-4" />
                          <div className="text-white text-xl font-semibold">
                            Acceso Instantáneo
                          </div>
                          <p className="text-blue-200 text-sm mt-2">
                            Tu video se cargará aquí
                          </p>
                        </div>
                      </div>
                    </video>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center p-8">
                        <div className="relative mb-6">
                          <div className="w-32 h-32 rounded-full bg-gradient-to-r from-blue-500/20 to-indigo-500/20 animate-pulse mx-auto"></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <LogIn className="h-16 w-16 text-white/70" />
                          </div>
                        </div>
                        <div className="text-white text-xl font-semibold mb-2">
                          Demostración del Login
                        </div>
                        <p className="text-blue-200 text-sm">
                          (Agrega tu video login-demo.mp4 en la carpeta de
                          videos)
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-transparent to-transparent flex flex-col justify-end p-8">
                    <div className="flex flex-col md:flex-row items-end md:items-center justify-between gap-6">
                      <div className="text-white">
                        <h3 className="text-2xl md:text-3xl font-bold mb-2 tracking-tight">
                          ¿Ves lo fácil que es?
                        </h3>
                        <div className="flex items-center gap-2 text-slate-300">
                          <Shield className="h-5 w-5 text-emerald-400" />
                          <span className="font-medium">
                            Acceso seguro y encriptado
                          </span>
                        </div>
                      </div>

                      <Link
                        to="/register"
                        className="group inline-flex items-center gap-3 px-8 py-3 bg-white text-slate-900 rounded-full font-bold text-lg hover:bg-slate-100 transition-all shadow-xl hover:shadow-2xl hover:scale-105"
                      >
                        Comenzar ahora
                        <ArrowRight className="h-5 w-5 group-hover:translate-x-2 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Info debajo del video */}
                <div className="bg-white p-8 border-t border-slate-100">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
                        <LogIn className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 mb-1">
                          Inicio Rápido
                        </h4>
                        <p className="text-sm text-slate-600">
                          Accede a tu CRM en segundos desde cualquier
                          dispositivo.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 flex-shrink-0">
                        <Shield className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 mb-1">
                          Seguridad Total
                        </h4>
                        <p className="text-sm text-slate-600">
                          Tus datos están protegidos con encriptación de nivel
                          bancario.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center text-purple-600 flex-shrink-0">
                        <CheckCircle2 className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 mb-1">
                          Sin Complicaciones
                        </h4>
                        <p className="text-sm text-slate-600">
                          Interfaz intuitiva diseñada para la máxima
                          productividad.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-5">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-5">
            <div className="flex gap-8 md:gap-12">
              <div className="text-center md:text-left">
                <p className="text-white text-sm font-medium mb-2">Contacto</p>
                <a
                  href="mailto:ventas@kerbrumagency.com"
                  className="text-sm hover:text-white transition-colors"
                >
                  ventas@kerbrumagency.com
                </a>
              </div>
              <div className="text-center md:text-left">
                <p className="text-white text-sm font-medium mb-2">Web</p>
                <a
                  href="https://kerbrumagency.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm hover:text-white transition-colors"
                >
                  kerbrumagency.com
                </a>
              </div>
            </div>

            <div className="text-sm text-slate-500 mt-2 md:mt-0">
              © {new Date().getFullYear()} Kersales CRM
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicPlansPage;
